import React from 'react';
import {
  Box, FormControlLabel, Paper, Slider, Switch, TextField, Typography
} from '@mui/material';
import { PlaybookDetection } from '../../types/discoveryPlaybook';

interface PlaybookDetectionSettingsProps {
  detection: PlaybookDetection;
  onChange: (detection: PlaybookDetection) => void;
}

interface ThresholdRow {
  field: keyof PlaybookDetection;
  label: string;
  help: string;
}

/**
 * The three confidence bars, in the order they matter.
 *
 * They are not one number because the costs are not symmetric: a missed
 * guardrail costs one coaching moment, while a false one interrupts the rep
 * with a sound and spends the overlay's credibility for the rest of the call.
 * Hence guardrails sit ABOVE the bar for ticking an item.
 */
const THRESHOLDS: readonly ThresholdRow[] = [
  {
    field: 'guardrailMinConfidence',
    label: 'Guardrail fires at',
    help: 'Highest bar of the three. A false guardrail that beeps costs far more than a missed one.'
  },
  {
    field: 'hitMinConfidence',
    label: 'Item ticks solid at',
    help: 'At or above this the item shows as hit.'
  },
  {
    field: 'likelyMinConfidence',
    label: 'Item shows as "likely" at',
    help: 'Between this and the solid bar the rep sees a tappable ◍. This is what lets live detection tune for recall and leave precision to the post-call grader.'
  }
];

/**
 * Detection thresholds for the live checklist.
 *
 * Stored on the playbook document rather than in service configuration, so they
 * version with the content and can be tuned per tenant without a redeploy.
 */
const PlaybookDetectionSettings: React.FC<PlaybookDetectionSettingsProps> = ({
  detection,
  onChange
}) => {
  const update = (patch: Partial<PlaybookDetection>) => onChange({ ...detection, ...patch });

  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 3 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Detection sensitivity
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        How confident the coach has to be before it says anything. These apply to
        live calls only — the post-call score is unaffected.
      </Typography>

      {THRESHOLDS.map((row) => (
        <Box key={row.field} mb={2.5}>
          <Typography variant="caption" fontWeight={600}>
            {row.label} {Math.round(((detection?.[row.field] as number) ?? 0) * 100)}%
          </Typography>
          <Slider
            value={(detection?.[row.field] as number) ?? 0}
            min={0}
            max={1}
            step={0.05}
            marks
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
            onChange={(_, value) => update({ [row.field]: value as number } as Partial<PlaybookDetection>)}
            aria-label={row.label}
          />
          <Typography variant="caption" color="text.secondary">
            {row.help}
          </Typography>
        </Box>
      ))}

      <Box display="flex" gap={2} flexWrap="wrap" mt={1}>
        <TextField
          size="small"
          type="number"
          label="Warn cooldown (seconds)"
          helperText="Minimum gap between non-critical guardrails, so a rough patch coaches rather than buries. Criticals ignore it."
          value={detection?.guardrailWarnCooldownSeconds ?? 120}
          onChange={(event) =>
            update({ guardrailWarnCooldownSeconds: Number(event.target.value) })
          }
          sx={{ maxWidth: 280 }}
        />
        <TextField
          size="small"
          type="number"
          label="Warn auto-dismiss (seconds)"
          helperText="How long a warn stays on screen. Criticals wait for acknowledgement."
          value={detection?.guardrailWarnAutoDismissSeconds ?? 10}
          onChange={(event) =>
            update({ guardrailWarnAutoDismissSeconds: Number(event.target.value) })
          }
          sx={{ maxWidth: 280 }}
        />
      </Box>

      <FormControlLabel
        sx={{ mt: 2 }}
        control={
          <Switch
            checked={detection?.guardrailSoundEnabled ?? true}
            onChange={(event) => update({ guardrailSoundEnabled: event.target.checked })}
          />
        }
        label="Play an alert sound on the desktop when a guardrail fires"
      />
    </Paper>
  );
};

export default PlaybookDetectionSettings;
